import { useStaticCallback } from '@sparemin/blockhead';
import React, { useCallback, useEffect, useRef } from 'react';
import { VideoEventEmitter } from './types';

export type UseSeekedProps = {
  emitter: VideoEventEmitter;
  videoRef: React.RefObject<HTMLVideoElement>;
};

/**
 * handles emitting "seeked" events for the SegmentedVideoPlayer.
 * Suppresses "seeked" events from internal gap-skipping operations
 */
export default function useSeeked({ emitter, videoRef }: UseSeekedProps) {
  const isSeekedSuppressed = useRef(false);

  // expose a function to mark a seek as internal
  const suppressNextSeeked = useCallback(() => {
    isSeekedSuppressed.current = true;
  }, []);

  const allowNextSeeked = useCallback(() => {
    isSeekedSuppressed.current = false;
  }, []);

  const handleSeeked = useStaticCallback(() => {
    if (isSeekedSuppressed.current) {
      allowNextSeeked();
      return;
    }

    emitter.emit('seeked', new Event('seeked'));
  });

  useEffect(() => {
    const video = videoRef.current;
    if (!video) {
      return undefined;
    }

    video.addEventListener('seeked', handleSeeked);

    return () => {
      video.removeEventListener('seeked', handleSeeked);
    };
  }, [handleSeeked, videoRef]);

  return { suppressNextSeeked, allowNextSeeked };
}
